import React, { useState, useEffect } from "react";
import Modal from "components/Models/index";
import Button from "components/Button/index";
import Input from "components/Input/index";
import { Item } from "@induz/sdk";
import "./MoveAndCopyOptions.scss";

const MoveCopyOptions = ({ isOpen, onClose, onRename, fileName, destinationPath }) => {
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    setNewName(""); // Clear input when a new conflict is presented
    setError("");
  }, [fileName]);

  const getFinalName = (name) => {
    if (!name) return "";
    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex !== -1) {
      const extension = fileName.slice(dotIndex);
      return name.includes(".") ? name : `${name}${extension}`;
    }
    return name;
  };

  const checkIfNameExists = async (name) => {
    if (!name || !destinationPath) return false;
    
    try {
      // Normalize path to avoid double slashes
      const cleanPath = destinationPath.endsWith("/") ? destinationPath : `${destinationPath}/`;
      const fullPath = `${cleanPath}${name}`;
      const exists = await Item.getItem(fullPath, false);
      return !!exists;
    } catch (error) {
      // If item doesn't exist, getItem might throw error
      return false;
    }
  };

  const handleNameChange = async (value) => {
    const sanitizedValue = value.replace(/[^a-zA-Z0-9 _\-().]/g, "");
    setNewName(sanitizedValue);
    setError("");

    const trimmedValue = sanitizedValue.trim();
    if (trimmedValue.length === 0) return;

    // Fast local check against the original conflicting name
    const finalName = getFinalName(trimmedValue);
    if (finalName === fileName.trim()) {
      setError(`"${finalName}" already exists. Please choose a different name.`);
      return;
    }

    // Check if the new name also exists via API
    setIsChecking(true);
    const exists = await checkIfNameExists(finalName);
    setIsChecking(false);
    
    if (exists) {
      setError(`"${finalName}" already exists. Please choose a different name.`);
    }
  };

  const handleConfirm = async () => {
    const trimmedName = newName.trim();
    if (trimmedName.length === 0) return;

    const finalName = getFinalName(trimmedName);

    // Final check before confirming
    if (finalName === fileName.trim()) {
      setError(`"${finalName}" already exists. Please choose a different name.`);
      return;
    }

    setIsChecking(true);
    const exists = await checkIfNameExists(finalName);
    setIsChecking(false);

    if (exists) {
      setError(`"${finalName}" already exists. Please choose a different name.`);
      return;
    }

    onRename(newName);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && newName.length > 0 && !error && !isChecking) {
      handleConfirm();
    }
  };

  return (
    <Modal
      heading={`"${fileName}" already exists`}
      isVisible={isOpen}
      handleCancel={onClose}
      showFooter={false}
    >
      <div className="moveCopyOptionsContainer">
        <p>Please enter a new name for the file or folder.</p>
        <Input
          placeholder="Enter new name"
          value={newName}
          onChange={(e) => handleNameChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {isChecking && (
          <p style={{ color: "#f39c12", fontSize: "14px", marginTop: "5px" }}>
            Checking if name exists...
          </p>
        )}
        {error && (
          <p style={{ color: "#e74c3c", fontSize: "14px", marginTop: "5px" }}>
            {error}
          </p>
        )}
        <div className="buttons">
          <Button title="Cancel" type="violet" onClick={onClose} />
          <Button
            title="Confirm"
            onClick={handleConfirm}
            type="violet"
            disabled={newName.length === 0 || !!error || isChecking}
          />
        </div>
      </div>
    </Modal>
  );
};

export default MoveCopyOptions;